import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { normalizeCrmConfig } from '@leads-platform/shared';
import type { CampaignResponse, RouteRuleRowResponse } from '@leads-platform/shared';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { StatusPill } from '../../components/ui/status-pill';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Toggle } from '../../components/ui/toggle';
import { useToast } from '../../components/ui/toast';
import { campaignStyleEnumToLabel } from '../../lib/campaign-styles';
import { ApiError, deleteRouteRule, getRouteRules, upsertRouteRule } from './api';

/**
 * One error line from anything this screen can throw: an `ApiError` renders its
 * message plus any per-field validation errors; anything else degrades to the
 * message, then the caller's fallback.
 */
function describeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const fieldErrors = err.validationErrors
      ? Object.values(err.validationErrors).flat().join(' ')
      : '';
    return [err.message, fieldErrors].filter(Boolean).join(': ') || fallback;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/**
 * The row's set conditions rendered as the one sentence ops reads (§3.10's
 * "status is 200 AND body does not contain duplicate" — conditions are ANDed;
 * unset fields are skipped, the api forbids a rule with none).
 */
function conditionSentence(
  rule: Pick<RouteRuleRowResponse, 'matchStatus' | 'bodyContains' | 'bodyNotContains'>
): string {
  const parts: string[] = [];
  if (rule.matchStatus !== null && rule.matchStatus !== undefined) {
    parts.push(`status = ${rule.matchStatus}`);
  }
  if (rule.bodyContains !== null && rule.bodyContains !== undefined) {
    parts.push(`body contains "${rule.bodyContains}"`);
  }
  if (rule.bodyNotContains !== null && rule.bodyNotContains !== undefined) {
    parts.push(`body does not contain "${rule.bodyNotContains}"`);
  }
  return parts.join(' AND ');
}

/**
 * A campaign option — both selects render the same `name (#id · style ·
 * status)` line so ops can tell same-named campaigns apart.
 */
interface CampaignOption {
  id: string;
  name: string;
  label: string;
}

function toCampaignOption(campaign: CampaignResponse): CampaignOption {
  const style = campaign.campaignStyle ? campaignStyleEnumToLabel(campaign.campaignStyle) : '—';
  return {
    id: campaign.id,
    name: campaign.name,
    label: `${campaign.name} (#${campaign.id} · ${style} · ${campaign.status})`,
  };
}

/**
 * Can a route rule live on this campaign (or point at it)? Both selects list
 * campaigns that RECEIVE a partner post: the rule is evaluated on the response
 * such a post got — `routeAfterSend` is keyed on the candidate the send went
 * TO (distribution.handler.ts) — so a capture campaign (paid_social /
 * cto_email) never qualifies: it only sources leads. The receivers are `co_op`
 * sponsors (the §5 chain's candidates) and `custom_optin` campaigns whose
 * resolved send_mode is `lead_post` (the campaign is its own single candidate).
 * The resolution mirrors the distribution handler's `resolveCrmConfig`: an
 * absent send_mode defaults to lead_post, an unparseable config rules the
 * campaign out entirely.
 */
function isRoutableCampaign(campaign: CampaignResponse): boolean {
  if (campaign.campaignStyle === 'co_op') return true;
  if (campaign.campaignStyle !== 'custom_optin') return false;
  try {
    return (normalizeCrmConfig(campaign.crmConfig).send_mode ?? 'lead_post') === 'lead_post';
  } catch {
    return false;
  }
}

/**
 * The dialog's fields. `matchStatus` is the raw `<input type="number">` text
 * ('' = unset); the three condition fields become the shared upsert body's
 * nullable values on submit.
 */
interface RuleFormData {
  campaignId: string;
  matchStatus: string;
  bodyContains: string;
  bodyNotContains: string;
  destinationCampaignId: string;
  enabled: boolean;
}

const emptyForm: RuleFormData = {
  campaignId: '',
  matchStatus: '',
  bodyContains: '',
  bodyNotContains: '',
  destinationCampaignId: '',
  enabled: true,
};

/**
 * Post-response route rules (FR-B30 / Q72, release item 3a config half) —
 * every campaign's rule in one table: source → condition → destination →
 * enabled. One rule per campaign (the api upserts on PUT), depth 1, no
 * chaining. The campaign form's own control is epic 034 (ADR-010 §11).
 */
export function RouteRules() {
  const [rules, setRules] = useState<RouteRuleRowResponse[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignResponse[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<RouteRuleRowResponse | null>(null); // null = Add
  const [form, setForm] = useState<RuleFormData>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [deleting, setDeleting] = useState<RouteRuleRowResponse | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  const { success, error: toastError } = useToast();

  const loadRules = () => {
    getRouteRules()
      .then(setRules)
      .catch((err: unknown) => setError(describeError(err, 'Failed to load route rules')));
  };

  useEffect(() => {
    api
      .getCampaigns()
      .then(setCampaigns)
      .catch(() => {
        // Best-effort: the dialog selects just start empty.
      });
    loadRules();
  }, []);

  const campaignOptions = [...campaigns]
    .filter(isRoutableCampaign)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(toCampaignOption);
  const destinationOptions = campaignOptions.filter((option) => option.id !== form.campaignId);

  // Names resolve against the unfiltered list so a legacy rule hanging on a
  // campaign the filter hides still reads as a name, not `#id`.
  const campaignName = (id: string): string =>
    campaigns.find((campaign) => campaign.id === id)?.name ?? `#${id}`;

  const openAdd = () => {
    setEditingRule(null);
    setForm(emptyForm);
    setFormError(null);
    setIsEditorOpen(true);
  };

  const openEdit = (rule: RouteRuleRowResponse) => {
    setEditingRule(rule);
    setForm({
      campaignId: rule.campaignId,
      matchStatus:
        rule.matchStatus === null || rule.matchStatus === undefined ? '' : String(rule.matchStatus),
      bodyContains: rule.bodyContains ?? '',
      bodyNotContains: rule.bodyNotContains ?? '',
      destinationCampaignId: rule.destinationCampaignId,
      enabled: rule.enabled,
    });
    setFormError(null);
    setIsEditorOpen(true);
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setEditingRule(null);
    setForm(emptyForm);
    setFormError(null);
  };

  const handleEditorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.campaignId === '') {
      setFormError('Pick a source campaign');
      return;
    }
    if (form.destinationCampaignId === '') {
      setFormError('Pick a destination campaign');
      return;
    }
    // The two checks the api enforces, mirrored client-side: ≥ 1 condition and
    // destination ≠ source (shared upsert schema's superRefine; service 422).
    const matchStatusText = form.matchStatus.trim();
    if (matchStatusText !== '' && Number.isNaN(Number(matchStatusText))) {
      setFormError('Match status must be a number');
      return;
    }
    const matchStatus = matchStatusText === '' ? null : Number(matchStatusText);
    const bodyContains = form.bodyContains.trim() === '' ? null : form.bodyContains.trim();
    const bodyNotContains = form.bodyNotContains.trim() === '' ? null : form.bodyNotContains.trim();
    if (matchStatus === null && bodyContains === null && bodyNotContains === null) {
      setFormError('At least one condition is required');
      return;
    }
    if (form.destinationCampaignId === form.campaignId) {
      setFormError('Destination must differ from the source campaign');
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      const saved = await upsertRouteRule(form.campaignId, {
        matchStatus,
        bodyContains,
        bodyNotContains,
        destinationCampaignId: form.destinationCampaignId,
        enabled: form.enabled,
      });
      success(
        `Route rule for "${campaignName(form.campaignId)}" saved → ${saved.destinationCampaignName}`
      );
      closeEditor();
      loadRules();
    } catch (err) {
      setFormError(describeError(err, 'Failed to save the route rule'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleting === null) return;
    setDeletingBusy(true);
    try {
      await deleteRouteRule(deleting.campaignId);
      success(`Route rule for "${deleting.campaignName}" deleted`);
      setDeleting(null);
      loadRules();
    } catch (err) {
      toastError(describeError(err, 'Failed to delete the route rule'));
    } finally {
      setDeletingBusy(false);
    }
  };

  if (error) {
    return (
      <div>
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          Error loading route rules: {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Route Rules</h1>
          <p className="text-muted-foreground mt-2">
            After the first advertiser responds, a matching lead is sent once more to the
            destination campaign and counted there instead. One rule per campaign; no chaining.
          </p>
        </div>
        <Button onClick={openAdd}>Add rule</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Rules</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead>
                <TableHead>Condition</TableHead>
                <TableHead>Destination</TableHead>
                <TableHead>Enabled</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    No route rules. Add one to send a lead that matches the condition to a second
                    campaign after the first advertiser responds.
                  </TableCell>
                </TableRow>
              ) : (
                rules.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell className="font-mono font-medium">{rule.campaignName}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {conditionSentence(rule) || '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {rule.destinationCampaignName}
                    </TableCell>
                    <TableCell>
                      <StatusPill value={rule.enabled ? 'Active' : 'Paused'} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(rule)}>
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleting(rule)}>
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog
        open={isEditorOpen}
        onOpenChange={(open) => {
          if (!open) closeEditor();
        }}
      >
        {/* The five-field form outgrows short viewports — let the content
            scroll so the footer buttons stay reachable (the shared Dialog has
            no max-height of its own). */}
        <DialogContent className="max-h-[calc(100vh-4rem)] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingRule ? `Edit Route Rule — ${editingRule.campaignName}` : 'Add Route Rule'}
            </DialogTitle>
            <DialogDescription>
              All set conditions must match the advertiser's response. The rule is keyed by its
              source campaign — saving again replaces it.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditorSubmit}>
            <div className="space-y-4 py-4">
              {formError && <p className="text-sm text-destructive">{formError}</p>}
              <div className="space-y-2">
                <Label htmlFor="source-campaign">Source campaign</Label>
                <Select
                  id="source-campaign"
                  value={form.campaignId}
                  onChange={(e) => setForm({ ...form, campaignId: e.target.value })}
                  disabled={editingRule !== null}
                  required
                >
                  <option value="" disabled>
                    Select campaign…
                  </option>
                  {campaignOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                {editingRule && (
                  <p className="text-xs text-muted-foreground">
                    The rule belongs to this campaign — it cannot move to another one.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="match-status">Match status (optional)</Label>
                <Input
                  id="match-status"
                  type="number"
                  value={form.matchStatus}
                  onChange={(e) => setForm({ ...form, matchStatus: e.target.value })}
                  placeholder="e.g., 200"
                />
                <p className="text-xs text-muted-foreground">
                  The advertiser HTTP status to match — e.g. 200.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="body-contains">Body contains (optional)</Label>
                <Input
                  id="body-contains"
                  value={form.bodyContains}
                  onChange={(e) => setForm({ ...form, bodyContains: e.target.value })}
                  placeholder='e.g., "duplicate"'
                />
                <p className="text-xs text-muted-foreground">
                  Matched case-insensitively anywhere in the response body.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="body-not-contains">Body does not contain (optional)</Label>
                <Input
                  id="body-not-contains"
                  value={form.bodyNotContains}
                  onChange={(e) => setForm({ ...form, bodyNotContains: e.target.value })}
                  placeholder='e.g., "duplicate"'
                />
                <p className="text-xs text-muted-foreground">
                  The rule only matches while this text is absent from the response body.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="destination-campaign">Destination campaign</Label>
                <Select
                  id="destination-campaign"
                  value={form.destinationCampaignId}
                  onChange={(e) => setForm({ ...form, destinationCampaignId: e.target.value })}
                  required
                >
                  <option value="" disabled>
                    Select campaign…
                  </option>
                  {destinationOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                <p className="text-xs text-muted-foreground">
                  Mapping, auth and caps are inherited from this campaign — the routed lead counts
                  towards it.
                </p>
              </div>
              <Toggle
                checked={form.enabled}
                onChange={(checked) => setForm({ ...form, enabled: checked })}
                label="Enabled"
                hint="A disabled rule never routes."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeEditor}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {deleting ? `Delete the rule for "${deleting.campaignName}"?` : 'Delete'}
            </DialogTitle>
            <DialogDescription>
              The campaign's leads stop being routed to {deleting?.destinationCampaignName}. This
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleting(null)}
              disabled={deletingBusy}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deletingBusy}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
